import { useContext, useEffect, useState } from 'react';
import ModalComponent from 'components/Common/Modal';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import dayjs from 'dayjs';
import { store } from 'components/Store';
import { useRef } from 'react';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import InteractionFormFields, { InteractionFormFieldsSkeleton } from '../InteractionFormFields';

const LogInteractionModal = ({
  onClose,
  onSuccess,
  title: modalTitle,
  preselectedStakeholders = [],
  interactionId,
}) => {
  const isEditing = !!interactionId;
  const { state } = useContext(store);
  const { agencyUser, newAgencyPlan } = useTopicOrClientWord();
  const defaultTitle =
    agencyUser || newAgencyPlan ? `Log an interaction for ${state.team?.teamName}` : 'Log an interaction';
  const { myself } = state.team ?? {};
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    date: dayjs().format('YYYY-MM-DD'),
    type: '',
    description: '',
    initiator: 'Organisation',
    stakeholders: preselectedStakeholders,
    members: myself ? [{ value: myself.id, id: myself.id, label: myself.fullName ?? myself.emailAddress }] : [],
  });

  const { generalApiCall } = useGeneralApiCall();

  const initialFormRef = useRef(null);
  const loadInteractionData = useRef();
  loadInteractionData.current = async (source) => {
    try {
      const result = await generalApiCall({
        method: 'get',
        pathname: `/api/crm-interaction/${interactionId}`,
        needsAuthentication: true,
        requestSource: source,
        notShowErrorMessage: true,
      });
      if (result) {
        const loadedForm = {
          title: result.title ?? '',
          date: result.dateTime ? dayjs(result.dateTime).format('YYYY-MM-DD') : '',
          type: result.interactionType ?? '',
          description: result.htmlBody ?? result.description ?? '',
          initiator:
            result.initiatedByOrganisation === true
              ? 'Organisation'
              : result.initiatedByOrganisation === false
                ? 'Stakeholder'
                : '',
          stakeholders: (result.stakeholderContacts ?? []).map((c) => ({
            value: c.id,
            label: c.fullName ?? '',
            id: c.id,
            email: '',
            imageId: c.imageId ?? null,
            imageUrl: null,
            stakeholderContactId: c.stakeholderContactId ?? null,
            party: c.party ?? null,
          })),
          members: (result.associatedUserIds ?? []).map((id, idx) => {
            const nameFromResponse = result.associatedUserFullNames?.[idx];
            const member = !nameFromResponse ? state.team?.allMembers?.find((m) => m.id === id) : null;
            return { value: id, label: nameFromResponse ?? member?.fullName ?? member?.emailAddress ?? `User ${id}` };
          }),
        };
        setForm(loadedForm);
        initialFormRef.current = loadedForm;
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isEditing) return;
    const source = new AbortController();
    loadInteractionData.current(source);
    return () => source.abort('Interaction edit form cancelled');
  }, [interactionId]);

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const isFormValid = !!form.title.trim() && !!form.type && !!form.date;
  const isDirty = !isEditing || JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      createNotification('danger', 'Please enter a title');
      return;
    }
    if (!form.type) {
      createNotification('danger', 'Please select an interaction type');
      return;
    }
    if (!form.date) {
      createNotification('danger', 'Please select a date');
      return;
    }

    setIsSubmitting(true);
    const response = await generalApiCall({
      method: 'post',
      pathname: isEditing ? '/api/crm-interaction/update' : '/api/crm-interaction/create',
      requestProperties: {
        ...(isEditing && { id: Number(interactionId) }),
        title: form.title,
        date: form.date,
        type: form.type,
        description: form.description,
        initiatedByOrganisation:
          form.initiator === 'Organisation' ? true : form.initiator === 'Stakeholder' ? false : null,
        contactIds: form.stakeholders.map((p) => p.id),
        userIds: form.members.map((m) => m.value),
      },
      needsAuthentication: true,
      returnCompleteRequest: true,
    });
    setIsSubmitting(false);

    if (response?.status === 200) {
      createNotification('success', isEditing ? 'Interaction updated successfully' : 'Interaction logged successfully');
      onSuccess?.(response.data);
    }
  };

  return (
    <ModalComponent
      isOpen={true}
      setIsOpen={() => onClose()}
      maxWidth={700}
      height='90vh'
      notShowCloseButton={true}
      shouldCloseOnOverlayClick={false}
    >
      <div className='d-flex flex-column h-100'>
        <div className='px-4 py-3 border-bottom flex-centered justify-content-between flex-shrink-0'>
          <h3 className='mb-0 title-h5-m-bold'>{modalTitle ?? (isEditing ? 'Edit interaction' : defaultTitle)}</h3>
          <button className='circle-button general-button hovered-button action-state-hover' onClick={onClose}>
            <span className='icon-close paragraph-p5' />
          </button>
        </div>

        <CustomScrollbar className='simple-scrollbar indented-scrollbar-small flex-grow-1' maximalThumbYSize={200}>
          <div className='px-4 py-4'>
            {isLoading ? (
              <InteractionFormFieldsSkeleton />
            ) : (
              <InteractionFormFields form={form} onChange={handleChange} autoFocusTitle={!isEditing} />
            )}
          </div>
        </CustomScrollbar>

        <div className='px-4 py-3 border-top flex-shrink-0 d-flex justify-content-end'>
          <button
            className='general-button border rounded-pill py-2 px-4 paragraph-p2 mr-3'
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className='general-button action-button rounded-pill py-2 px-4 paragraph-p2'
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading || !isFormValid || !isDirty}
          >
            {isSubmitting ? (isEditing ? 'Saving...' : 'Logging...') : isEditing ? 'Save changes' : 'Log interaction'}
          </button>
        </div>
      </div>
    </ModalComponent>
  );
};

export default LogInteractionModal;
